import { VisualInspectionReturns } from "./VisualInspectionReturns";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router";
import { api } from "../api";
import { withBuildingScope } from "../ui/building-scope";
import { DateOnlyInput } from "../components/DateOnlyInput";
import { formatDateTime } from "../dates";
export function VisualInspectionFindings({
  data,
  onSaved,
  disabled,
}: {
  data: any;
  onSaved: () => Promise<unknown>;
  disabled: boolean;
}) {
  const a = data.assignment,
    latest = data.submissions[0],
    path = withBuildingScope(
      "/api/operations/visit-reports/" + a.id + "/follow-ups",
      a.development_id,
    );
  const [summary, setSummary] = useState<any>(),
    [message, setMessage] = useState(""),
    [busy, setBusy] = useState(false),
    [pendingView, setPendingView] = useState(false);
  const pending = useRef<
      { body: string; path: string; confirmed: boolean } | undefined
    >(undefined),
    inFlight = useRef(false);
  const load = async () => {
    const value = await api(path);
    setSummary(value);
  };
  useEffect(() => {
    let active = true;
    api(path)
      .then((value) => {
        if (active) setSummary(value);
      })
      .catch((e) => {
        if (active) setMessage(e.message);
      });
    return () => {
      active = false;
    };
  }, [path, a.version]);
  async function retry() {
    if (inFlight.current || !pending.current) return;
    inFlight.current = true;
    const actionPath = pending.current.path;
    setBusy(true);
    setMessage("");
    try {
      if (!pending.current.confirmed) {
        try {
          await api(
            withBuildingScope(
              "/api/operations/visit-reports/" +
                a.id +
                "/" +
                pending.current.path,
              a.development_id,
            ),
            { method: "POST", body: pending.current.body },
          );
          pending.current.confirmed = true;
        } catch (e: any) {
          if (e.statusCode >= 400 && e.statusCode < 500) {
            pending.current = undefined;
            setPendingView(false);
          }
          throw e;
        }
      }
      await onSaved();
      await load();
      pending.current = undefined;
      setPendingView(false);
      setMessage(
        actionPath === "follow-ups"
          ? "Follow-up saved. The original observation and open work are retained."
          : "Return inspection action saved. Original evidence and due dates are retained.",
      );
    } catch (e: any) {
      setMessage(e.message);
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  }
  function save(body: any, target = "follow-ups") {
    if (pending.current || inFlight.current) return;
    pending.current = {
      path: target,
      body: JSON.stringify({
        ...body,
        expectedVersion: summary.reportVersion,
        ...(target === "follow-ups"
          ? {
              submissionId: summary.submissionId,
              routingId: summary.routingId,
              checkId: summary.checkId,
            }
          : {}),
        idempotencyKey: crypto.randomUUID(),
      }),
      confirmed: false,
    };
    setPendingView(true);
    void retry();
  }
  const questions = latest
    ? a.definition.questions.filter((q: any) =>
        latest.issues.some(
          (i: any) => i.kind === "exception" && i.questionId === q.id,
        ),
      )
    : [];
  return (
    <section className="report-no-print" aria-label="Inspection findings">
      <h2>Findings and follow-up</h2>
      {message && <p role="status">{message}</p>}
      {pendingView && !busy && (
        <button type="button" onClick={() => void retry()}>
          {pending.current?.path === "follow-ups"
            ? "Retry finding handoff"
            : "Retry inspection action"}
        </button>
      )}
      {summary && (
        <>
          <p>
            {a.status === "Accepted" && summary.openCount
              ? "Inspection reviewed, actions outstanding."
              : "Inspection findings stay separate from report acceptance."}{" "}
            Use the usual urgent response for safety concerns.
          </p>
          {!summary.writesEnabled && (
            <p>Inspection follow-up changes are not enabled.</p>
          )}
          {summary.followUps.map((f: any) => (
            <article key={f.id}>
              <h3>
                {f.observation.label} · {f.purpose}
              </h3>
              <p>
                Submission {f.observation.revision} ·{" "}
                {f.observation.answer.notes ??
                  f.observation.answer.photoException?.reason}
              </p>
              <p>{f.observation.photos.length} retained photographs</p>
              <Link
                to={withBuildingScope(
                  f.action_id
                    ? "/work/actions/" + f.action_id
                    : "/work/cases/" + f.work_id,
                  a.development_id,
                )}
              >
                {f.action_id
                  ? f.purpose === "Coordination"
                    ? "Open Coordination Action"
                    : "Open Access Action"
                  : "Open Repairs Work"}
              </Link>
              <p>
                {f.action_status ?? f.work_status}
                {f.owner_name ? " · " + f.owner_name : ""}
                {f.due_at ? " · Due " + formatDateTime(f.due_at) : ""}
                {f.priority ? " · " + f.priority : ""}
              </p>
              {f.closure_criteria && <p>Closure: {f.closure_criteria}</p>}
            </article>
          ))}
          <VisualInspectionReturns
            assignment={a}
            latest={latest}
            summary={summary}
            disabled={disabled || busy || pendingView}
            onCommand={save}
          />
          {questions.map((q: any) => (
            <FindingForm
              key={latest.id + q.id}
              question={q}
              answer={latest.answers[q.id]}
              summary={summary}
              workOptions={data.workOptions}
              disabled={disabled || busy || pendingView}
              onSave={save}
            />
          ))}
        </>
      )}
    </section>
  );
}
function FindingForm({
  question: q,
  answer,
  summary,
  workOptions,
  disabled,
  onSave,
}: {
  question: any;
  answer: any;
  summary: any;
  workOptions: any[];
  disabled: boolean;
  onSave: (body: any) => void;
}) {
  const [purpose, setPurpose] = useState(""),
    [actionId, setActionId] = useState(""),
    [owner, setOwner] = useState(""),
    [due, setDue] = useState(""),
    [priority, setPriority] = useState("Normal"),
    [closure, setClosure] = useState("");
  const action = summary.actions.find((a: any) => a.id === actionId),
    saved = summary.followUps.some(
      (f: any) =>
        f.submission_id === summary.submissionId &&
        f.question_id === q.id &&
        f.purpose === purpose,
    );
  const eligible = summary.actions.filter(
    (a: any) =>
      !a.purposes.length || a.purposes.every((p: string) => p === purpose),
  );
  function chooseAction(id: string) {
    setActionId(id);
    const a = summary.actions.find((a: any) => a.id === id);
    setOwner(a?.assigned_to ?? "");
    setDue(a?.due_on ?? "");
    setPriority(a?.priority ?? "Normal");
    setClosure(a?.closure_criteria ?? "");
  }
  if (!summary.canCoordination && !summary.canAccess && !summary.canDefect)
    return null;
  return (
    <article aria-label={q.label + " finding"}>
      <h3>{q.label}</h3>
      <p>{answer.notes ?? answer.photoException?.reason}</p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const f = new FormData(e.currentTarget);
          onSave({
            questionId: q.id,
            purpose,
            reason: f.get("reason"),
            priority,
            ...(purpose === "Defect"
              ? { workId: f.get("workId") || null }
              : {
                  actionId: actionId || null,
                  expectedActionVersion: action?.version ?? null,
                  assignedTo: owner,
                  dueOn: due,
                  closureCriteria: closure,
                }),
          });
        }}
      >
        <fieldset disabled={disabled}>
          <legend>Choose follow-up</legend>
          <label>
            Finding type
            <select
              aria-label="Finding type"
              required
              value={purpose}
              onChange={(e) => {
                setPurpose(e.target.value);
                chooseAction("");
              }}
            >
              <option value="">Choose the appropriate route</option>
              {summary.canCoordination && (
                <option value="Coordination">
                  Coordination — waste, cleaning, supplier or evidence follow-up
                </option>
              )}
              {summary.canAccess &&
                (String(answer.value).startsWith("Unable") ||
                  answer.photoException) && (
                  <option value="Access">
                    Access — actual access restriction
                  </option>
                )}
              {summary.canDefect && answer.value === "Fail" && (
                <option value="Defect">Defect — Repairs Work</option>
              )}
            </select>
          </label>
          {purpose && (
            <>
              <label>
                Why this follow-up is needed
                <textarea
                  name="reason"
                  required
                  minLength={3}
                  maxLength={1000}
                />
              </label>
              <label>
                Priority
                <select
                  aria-label="Finding priority"
                  value={priority}
                  disabled={Boolean(action)}
                  onChange={(e) => setPriority(e.target.value)}
                >
                  {["Normal", "Low", "High", "Urgent"].map((p) => (
                    <option key={p}>{p}</option>
                  ))}
                </select>
              </label>
              {purpose === "Defect" ? (
                <label>
                  Repairs Work
                  <select name="workId" aria-label="Repairs Work">
                    <option value="">Create new Repairs Work</option>
                    {workOptions.map((w: any) => (
                      <option value={w.id} key={w.id}>
                        {w.title}
                      </option>
                    ))}
                  </select>
                </label>
              ) : (
                <>
                  <label>
                    Action
                    <select
                      aria-label="Follow-up Action"
                      value={actionId}
                      onChange={(e) => chooseAction(e.target.value)}
                    >
                      <option value="">Create new Action</option>
                      {eligible.map((a: any) => (
                        <option key={a.id} value={a.id}>
                          {a.title}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Responsible person
                    <select
                      aria-label="Action owner"
                      required
                      disabled={Boolean(action)}
                      value={owner}
                      onChange={(e) => setOwner(e.target.value)}
                    >
                      <option value="">Choose an eligible person</option>
                      {summary.owners.map((u: any) => (
                        <option key={u.id} value={u.id}>
                          {u.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Due date
                    <DateOnlyInput
                      required
                      disabled={Boolean(action)}
                      value={due}
                      onChange={setDue}
                    />
                  </label>
                  <label>
                    What must be done to close this Action
                    <textarea
                      required
                      minLength={10}
                      maxLength={4000}
                      disabled={Boolean(action)}
                      value={closure}
                      onChange={(e) => setClosure(e.target.value)}
                    />
                  </label>
                  {action && (
                    <p>
                      The existing Action is linked with its current
                      responsibility and closure details; these are not
                      overwritten.
                    </p>
                  )}
                </>
              )}
              <p>
                Use Access only for an actual access restriction. Weather or
                missing photographic evidence can need Coordination. A physical
                defect belongs in Repairs Work.
              </p>
              <button disabled={saved}>
                {saved
                  ? "Follow-up already linked"
                  : purpose === "Defect"
                    ? "Create or link Repairs Work"
                    : "Create or link Action"}
              </button>
            </>
          )}
        </fieldset>
      </form>
    </article>
  );
}
